import { Box, Paper } from "@mui/material";
import TimetableGrid from "./TimetableGrid";
import { useEffect, useState } from "react";
import {
  createTimeTableEntry,
  deleteTimeTableEntry,
  fetchAcademicyears,
  fetchClassSections,
  fetchTimeTableEntries,
  getUsers,
  updateTimeTableEntry,
} from "ServerAPI/serverAPI";
import { useCookies } from "react-cookie";
import { Spinner } from "react-bootstrap";
import TimeTableForm from "./TimeTableForm";
import { useToast } from "context/toast-context/ToastProvider";
import ToastMessage from "components/Dialog/ToastMessage";
import DeleteModalConfirmation from "components/Dialog/DeleteModalConfirmation";

const TimeTable = () => {
  const [loading, setLoading] = useState(false);
  const [timetableRows, setTimetableRows] = useState([]);
  const [cookies] = useCookies();
  const [classSections, setClassSections] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showToastMessage, setShowToastMessage] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [dataToDelete, setDataToDelete] = useState(null);
  const [form, setForm] = useState({
    coordinatorId: "",
    slotName: "",
  });
  const { showToast } = useToast();
  const [section, setSection] = useState("");
  const [selectedSection, setSelectedSection] = useState(null);

  const handleToastMessageClose = () => {
    setShowToastMessage(false);
  };

  const handleOpen = (slot, entry = null, timeTableId) => {
    setSelectedSlot({
      ...slot,
      timeTableId,
    });

    setSelectedEntry(entry);
    setOpen(true);
  };

  const handleClose = () => {
    setForm({
      coordinatorId: "",
      slotName: "",
    });
    setSelectedEntry(null);
    setSelectedSlot(null);
    setOpen(false);
  };

  const handleDeleteOpenModal = (payload) => {
    setDataToDelete(payload);
    setDeleteOpen(true);
  };

  const renderUserName = (timetableEntriesRes, usersData) => {
    const userMap = {};

    usersData?.forEach((u) => {
      userMap[u.userId] = u.name;
    });
    const updatedData = timetableEntriesRes?.data?.map((item) => {
      return {
        ...item,
        days: item.days?.map((day) => {
          return {
            ...day,
            slots: day.slots?.map((slot) => {
              return {
                ...slot,
                entries: slot.entries?.map((entry) => ({
                  ...entry,
                  coordinatorName:
                    userMap[entry.coordinatorId] || "Unknown Teacher",
                })),
              };
            }),
          };
        }),
      };
    });
    return updatedData;
  };

  const renderClassSections = (data, role, userId) => {
    if (!data || !role || !userId) return [];
    if (role === "organizationCoordinator") {
      return data;
    } else {
      return (
        data?.filter(
          (c) =>
            c.programCoordinators?.includes(userId) ||
            c.sectionCoordinator === userId,
        ) || []
      );
    }
  };

  const renderClassSectionUsers = () => {
    const classSection = classSections?.find(
      (c) =>
        c.classSectionId === selectedSection?.classSectionId ||
        c.classSectionId === section,
    );

    const usersIds = [
      ...(Array.isArray(classSection?.programCoordinators)
        ? classSection.programCoordinators
        : []),
      classSection?.sectionCoordinator,
    ].filter(Boolean);

    const sectionCoordinators = coordinators?.filter((c) =>
      usersIds.includes(c.userId),
    );

    return sectionCoordinators || [];
  };

  const fetchData = async () => {
    setLoading(true);

    const payload = {
      userId: cookies.userId,
      classSectionId: section,
    };

    const [timetableEntriesRes, classSectionRes, academicYearRes, usersRes] =
      await Promise.all([
        fetchTimeTableEntries(payload),
        fetchClassSections(payload),
        fetchAcademicyears(payload),
        getUsers(cookies.email, cookies.roleId, cookies.organizationId),
      ]);

    if (
      timetableEntriesRes?.status === "success" &&
      classSectionRes?.status === "success" &&
      academicYearRes?.status === "success" &&
      usersRes
    ) {
      const currentYear = academicYearRes?.data?.find(
        (a) => a.isCurrent === true,
      )?.academicYearId;

      setClassSections(
        classSectionRes?.data?.filter((c) => c.academicYear === currentYear) ||
          [],
      );
      const usersData = usersRes?.data?.users;

      setTimetableRows(renderUserName(timetableEntriesRes, usersData));
      setCoordinators(
        usersRes?.data?.users?.filter(
          (u) => u.role.roleId === "programCoordinator" && u.deleted === false,
        ) || [],
      );
      const selectedClassSection =
        timetableEntriesRes?.data?.[0]?.classSectionId;

      setSelectedSection(
        classSectionRes?.data?.find(
          (c) => c.classSectionId === selectedClassSection,
        ) || null,
      );
    } else {
      setTimetableRows([]);
      setClassSections([]);
      setCoordinators([]);
      setSelectedSection(null);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [cookies.userId, section]);

  const handleCreateEntry = async (payload) => {
    setButtonLoading(true);
    const response = await createTimeTableEntry(payload);
    if (response && response.status === "success") {
      setTimetableRows(renderUserName(response, coordinators));
      setOpen(false);
      setButtonLoading(false);
      showToast("Entry Created Successfully", "success");
    } else {
      setOpen(true);
      setButtonLoading(false);
      showToast(response?.message || "Failed to create Entry", "error");
    }
  };

  const handleUpdateEntry = async (payload) => {
    setButtonLoading(true);
    const response = await updateTimeTableEntry(payload);
    if (response && response.status === "success") {
      setTimetableRows(renderUserName(response, coordinators));
      setOpen(false);
      setButtonLoading(false);
      showToast("Entry Updated Successfully", "success");
    } else {
      setOpen(true);
      setButtonLoading(false);
      showToast(response?.message || "Failed to update Entry", "error");
    }
  };

  const handleDeleteEntry = async () => {
    setLoading(true);
    const response = await deleteTimeTableEntry(dataToDelete);
    if (response && response.status === "success") {
      setTimetableRows(renderUserName(response, coordinators));
      setDataToDelete(null);
      setDeleteOpen(false);
      setLoading(false);
      showToast("Entry Deleted Successfully", "success");
    } else {
      setDataToDelete(null);
      setDeleteOpen(false);
      setLoading(false);
      showToast(response?.message || "Failed to delete Entry", "error");
    }
  };

  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <Spinner animation="border" />
      </div>
    );

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <ToastMessage
          open={showToastMessage}
          message="Course Marked As Deleted"
          closeTiming={3000}
          handleClose={handleToastMessageClose}
        />
        <TimetableGrid
          timeTableData={timetableRows}
          handleOpen={handleOpen}
          handleDeleteOpenModal={handleDeleteOpenModal}
          cookies={cookies}
          section={section}
          setSection={setSection}
          onRefresh={fetchData}
          classSections={renderClassSections(
            classSections,
            cookies.roleId,
            cookies.userId,
          )}
          sectionName={
            classSections?.find((c) => c.classSectionId === section)?.title ||
            selectedSection
          }
        />
        <TimeTableForm
          open={open}
          onClose={handleClose}
          form={form}
          setForm={setForm}
          coordinators={renderClassSectionUsers()}
          selectedEntry={selectedEntry}
          selectedSlot={selectedSlot}
          buttonLoading={buttonLoading}
          cookies={cookies}
          onSubmit={selectedEntry ? handleUpdateEntry : handleCreateEntry}
        />
        <DeleteModalConfirmation
          deleteOpen={deleteOpen}
          setDeleteOpen={setDeleteOpen}
          deleteButtonText="Delete Entry"
          onConfirmDelete={handleDeleteEntry}
        />
      </Paper>
    </Box>
  );
};

export default TimeTable;
